import { apiClient } from '$lib/api/client';
import { authHeader } from '$lib/stores/auth';

/**
 * The result row a signed-in customer sees.
 *
 * Counts and indicators, and deliberately not one person's name, address or
 * telephone number — those belong to the profile, which is the route fair use
 * counts. See `docs/refactor/member-result-row.md`.
 */
export interface RoleCount {
  role: string;
  count: number;
}

export interface MemberRow {
  id: string;
  name: string;

  size?: string;
  size_estimated: boolean;

  zip?: string;

  city?: string;
  canton?: string;
  language?: string;

  /** The first thing the company's own website says it offers. */
  web_summary?: string;

  /** The harmonized roles present, with how many hold each. */
  roles: RoleCount[];

  /**
   * Everybody known there, including those whose role was never harmonized.
   * Not the sum of `roles` — most contacts in the corpus have no role at all.
   *
   * Named contact_count and not contacts: the company object already carries a
   * `contacts` array, the identifiers an order is built from, and a number
   * landing on that field when a row is merged would replace the order's
   * contents with its own length.
   */
  contact_count: number;

  has_web: boolean;
  has_phone: boolean;
  has_social: boolean;

  /**
   * Always true in practice, and stamped here rather than sent by the API.
   *
   * It is what `hydrate` reads to decide a company still needs fetching, so a
   * row arriving without it is a row that is asked for again on the next
   * render — and hydrating writes to the store the render reads, so "again"
   * means forever. See fetchMemberRows.
   *
   * Typed `boolean` and not the literal `true`: a row is merged onto a
   * `CompanyWithContacts`, whose own flag is a plain boolean because a
   * skeleton carries false. Narrowing it here would make the two shapes
   * incompatible for the one operation they exist to perform together.
   */
  hydrated: boolean;
}

/** How many rows one request asks for. Matches the preview block. */
export const memberBlockSize = 500;

/**
 * Fetch the rows for one block of companies.
 *
 * A fetch, not a search: no filters travel with it, so a block cannot come back
 * short and the list cannot develop a hole — the same property the preview
 * block fetch has, for the same reason.
 *
 * # Every row is stamped hydrated, as the preview block stamps its own
 *
 * This is not decoration. `hydrate` skips a company whose `hydrated` is true,
 * and that flag is the only thing that stops it asking again — the fetched
 * rows are merged into `mixedResults`, the results screen recomputes from
 * `mixedResults`, and recomputing calls `hydrate`. Without the stamp that is a
 * closed loop: the same block of identifiers requested for as long as the
 * screen is open, several times a second, all of them answered identically.
 *
 * It went unnoticed because the member row is the newer of the two shapes and
 * the flag belongs to neither the API nor the type it returns — fetchBlock had
 * always added it on the way past, and this function was written from the
 * endpoint rather than from its counterpart.
 */
export async function fetchMemberRows(companyIds: string[]): Promise<MemberRow[]> {
  if (companyIds.length === 0) return [];

  const response = await apiClient.post<{ companies: Omit<MemberRow, 'hydrated'>[] }>(
    '/deep-iq/rows',
    { company_ids: companyIds },
    { headers: authHeader() }
  );

  return (response.data.companies ?? []).map((company) => ({ ...company, hydrated: true }));
}
